import { useEffect, useState } from 'react';
import Image from 'next/image';
import { cn } from '@/lib/utils';
import { asset } from '@/lib/asset';

const HOUR_MS = 60 * 60 * 1000;

/** Same format ladder as the event page's CountdownTimer (widgets.tsx). */
export function formatTimeLeft(diffMs: number): string {
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  return `${minutes}m ${seconds}s`;
}

interface LockCountdownProps {
  /** Instant to count down to; renders `fallback` when null or already past. */
  deadline: string | Date | null;
  /** Copy before the ticking time, e.g. "Entry closes in" / "Round locks in". */
  label: string;
  className?: string;
  /** Rendered in place of the countdown once the deadline is null or has passed. */
  fallback?: React.ReactNode;
}

/**
 * Compact one-line countdown for event cards. Gold by default, oxblood +
 * pulse inside the final hour. Once the deadline passes (including a tick
 * that expires it while mounted), renders `fallback` instead — the
 * surrounding card supplies its own static copy, if any.
 */
export function LockCountdown({ deadline, label, className, fallback }: LockCountdownProps) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  if (!deadline) return <>{fallback}</>;
  const diff = new Date(deadline).getTime() - now;
  if (!Number.isFinite(diff) || diff <= 0) return <>{fallback}</>;
  const urgent = diff < HOUR_MS;

  return (
    <span
      className={cn(
        'flex items-center gap-1 text-xs font-medium tabular-nums',
        urgent ? 'text-oxblood animate-pulse' : 'text-gold',
        className,
      )}
    >
      <Image src={asset('assets/icons/pocket_watch.webp')} alt="" width={14} height={14} className="flex-shrink-0" />
      <span className="truncate">{label} {formatTimeLeft(diff)}</span>
    </span>
  );
}
